import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { Shield, Check, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InsuranceCategory {
  id: string;
  label: string;
  checked?: boolean;
}

interface InsuranceWheelChartProps {
  categories?: InsuranceCategory[];
  formValues?: Record<string, any>;
  className?: string;
}

const STATUS_COLORS = {
  covered: 'hsl(var(--chart-5))',     // green
  partial: 'hsl(var(--chart-1))',     // amber/warning
  missing: 'hsl(var(--destructive))', // red
};

export function InsuranceWheelChart({ categories = [], formValues = {}, className }: InsuranceWheelChartProps) {
  const chartData = useMemo(() => {
    // Default insurance categories if none provided
    const defaultCategories = [
      { id: 'haftpflicht', label: 'Haftpflicht' },
      { id: 'hausrat', label: 'Hausrat' },
      { id: 'kfz', label: 'KFZ' },
      { id: 'leben', label: 'Leben' },
      { id: 'kranken', label: 'Kranken' },
      { id: 'rechtsschutz', label: 'Rechtsschutz' },
    ];

    const cats = categories.length > 0 ? categories : defaultCategories;

    return cats.map(cat => {
      const value = formValues[cat.id];
      let status: 'covered' | 'partial' | 'missing' = 'missing';
      
      if (value === true || value === 'ja' || value === 'vorhanden') {
        status = 'covered';
      } else if (value === 'teilweise' || value === 'prüfen') {
        status = 'partial';
      }

      return {
        name: cat.label,
        value: 1, // Equal segments
        status,
        fill: STATUS_COLORS[status],
      };
    });
  }, [categories, formValues]);

  const summary = useMemo(() => {
    const covered = chartData.filter(d => d.status === 'covered').length;
    const partial = chartData.filter(d => d.status === 'partial').length;
    const missing = chartData.filter(d => d.status === 'missing').length;
    return { covered, partial, missing, total: chartData.length };
  }, [chartData]);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Wheel Chart */}
      <div className="relative h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              animationDuration={800}
              animationBegin={0}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} stroke="none" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        
        {/* Center Icon */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-background shadow-lg flex items-center justify-center">
            <Shield className="h-10 w-10 text-primary" />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 rounded-lg bg-chart-5/10">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Check className="h-4 w-4 text-chart-5" />
            <span className="text-lg font-bold text-chart-5">{summary.covered}</span>
          </div>
          <p className="text-xs text-muted-foreground">Abgesichert</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-chart-1/10">
          <div className="flex items-center justify-center gap-1 mb-1">
            <AlertTriangle className="h-4 w-4 text-chart-1" />
            <span className="text-lg font-bold text-chart-1">{summary.partial}</span>
          </div>
          <p className="text-xs text-muted-foreground">Teilweise</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-destructive/10">
          <div className="flex items-center justify-center gap-1 mb-1">
            <X className="h-4 w-4 text-destructive" />
            <span className="text-lg font-bold text-destructive">{summary.missing}</span>
          </div>
          <p className="text-xs text-muted-foreground">Lücke</p>
        </div>
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {chartData.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: item.fill }}
              />
              <span className="text-sm">{item.name}</span>
            </div>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              item.status === 'covered' && "bg-chart-5/20 text-chart-5",
              item.status === 'partial' && "bg-chart-1/20 text-chart-1",
              item.status === 'missing' && "bg-destructive/20 text-destructive"
            )}>
              {item.status === 'covered' ? 'Abgesichert' : item.status === 'partial' ? 'Teilweise' : 'Lücke'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
